import { computed, inject, Injectable, signal } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSelectChange } from '@angular/material/select';
import { signalMethod } from '@ngrx/signals';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import {
    DaysOfWeek,
    DivisionConfig,
    DivisionConfigData,
    DivisionScheduleForm,
    DivisionScheduleSettings,
    RequiredDivisionConfigData,
    DivisionConfigStatus,
} from '@r1-compass-library/models';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { formValueSignal } from '@r1-compass-library/utilities';
import { DateTime } from 'luxon';

@Injectable()
export class DivisionScheduleFormService {
    readonly #nonNullableFormBuilder = inject(NonNullableFormBuilder);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #globalDivisionStore = inject(GlobalDivisionsStore);

    public checkedDays: DaysOfWeek[] = [];
    public displayTime = signal('');
    public displayOffset = signal('');

    public scheduleFormGroup = this.#nonNullableFormBuilder.group<DivisionScheduleForm>({
        offset: this.#nonNullableFormBuilder.control(''),
        startTime: this.#nonNullableFormBuilder.control(''),
        daysOfWeek: this.#nonNullableFormBuilder.control<DaysOfWeek[]>([]),
    });

    private scheduleFormGroupValue = formValueSignal<{ offset: string; startTime: string; daysOfWeek: DaysOfWeek[] }>(
        this.scheduleFormGroup
    );

    public saveDisabled = computed<boolean>(() => {
        const formValue = this.scheduleFormGroupValue();
        const startTime = formValue.startTime;
        const offset = formValue.offset;
        const checkedDays = formValue.daysOfWeek;
        return startTime == '' || offset == '' || checkedDays.length == 0;
    });

    constructor() {
        this.setDivisionScheduleFormData(this.#globalLoggedInUserStore.selectedDivisionConfig);
    }

    public updateDaysOfTheWeekCheckbox(matCheckboxChange: MatCheckboxChange, day: DaysOfWeek) {
        if (matCheckboxChange.checked && !this.isInCheckedDays(day)) {
            this.checkedDays.push(day);
        } else if (!matCheckboxChange.checked && this.isInCheckedDays(day)) {
            const checkedDayIndex = this.checkedDays.findIndex(
                checkedDay => checkedDay.toLowerCase() === day.toLowerCase()
            );
            this.checkedDays.splice(checkedDayIndex, 1);
        }
        this.scheduleFormGroup.patchValue({ daysOfWeek: this.checkedDays });

        this.scheduleFormGroup.markAllAsDirty();
        this.scheduleFormGroup.markAllAsTouched();
    }

    public isInCheckedDays(day: string): boolean {
        return this.checkedDays.findIndex(checkedDay => checkedDay.toLowerCase() === day.toLowerCase()) > -1;
    }

    /**
     * Method to transform user selected offset into UTC format.
     * @param event User selected offset
     */
    public transformOffset(event: MatSelectChange) {
        this.scheduleFormGroup.patchValue({ offset: event.value });

        this.scheduleFormGroup.markAllAsDirty();
        this.scheduleFormGroup.markAllAsTouched();
    }

    public transformTimeFormat(time: string) {
        const twentyFourHourTime = this.transformTo24Hr(time);
        this.scheduleFormGroup.patchValue({ startTime: twentyFourHourTime });
        this.scheduleFormGroup.markAllAsDirty();
        this.scheduleFormGroup.markAllAsTouched();
    }

    public saveDivisionSchedule() {
        const schedule: DivisionScheduleSettings = this.scheduleFormGroup.getRawValue();
        const divisionId = this.#globalLoggedInUserStore.selectedDivisionConfig()?.id;
        const loggedInUserId = this.#globalLoggedInUserStore.selectedUserId();

        if (divisionId && loggedInUserId) {
            const divisionConfig: RequiredDivisionConfigData = {
                id: divisionId,
                updatedBy: loggedInUserId,
            };
            const divisionConfigData: Partial<DivisionConfigData> = {
                schedule: schedule,
            };

            return this.#globalDivisionStore.saveDivisionConfigAndData({
                config: divisionConfig,
                data: divisionConfigData,
            });
        } else {
            return noopFormSaveMutationResult();
        }
    }

    public togglePause(statusToSet: DivisionConfigStatus) {
        const divisionId = this.#globalLoggedInUserStore.selectedDivisionConfig()?.id;
        const loggedInUserId = this.#globalLoggedInUserStore.selectedUserId();
        if (divisionId && loggedInUserId) {
            const divisionConfig: RequiredDivisionConfigData & { status: DivisionConfigStatus } = {
                id: divisionId,
                updatedBy: loggedInUserId,
                status: statusToSet,
            };
            return this.#globalDivisionStore.saveDivisionConfig({ config: divisionConfig });
        } else {
            return noopFormSaveMutationResult();
        }
    }

    private setDivisionScheduleFormData = signalMethod<DivisionConfig | undefined>(division => {
        if (division) {
            const scheduleData = division.config?.schedule;
            if (scheduleData) {
                this.scheduleFormGroup.patchValue({
                    offset: scheduleData.offset ?? '+00:00',
                    startTime: scheduleData.startTime ?? '00:00',
                    daysOfWeek: scheduleData.daysOfWeek ?? [],
                });
                scheduleData.daysOfWeek?.forEach(e => {
                    this.checkedDays.push(e);
                });
                this.displayTime.set(this.transformToAmPm(this.scheduleFormGroup.controls.startTime.value));
                this.displayOffset.set(this.scheduleFormGroup.controls.offset.value);
            }
        }
    });

    private transformTo24Hr(time: string) {
        return DateTime.fromFormat(time, 'h:mm a').toFormat('HH:mm');
    }

    private transformToAmPm(time: string): string {
        if (time) {
            return DateTime.fromFormat(time, 'HH:mm').toFormat('h:mm a');
        } else {
            return '';
        }
    }
}
